import { Snapshot } from "@/stores/snapshot";

export enum ConnectionStatus {
  // Connection to Pantheon API is stable
  CONNECTED = "CONNECTED",
  // Connection to Pantheon API is not stable, e.g. offline or server down
  DISCONNECTED = "DISCONNECTED",
}

/**
 * App wide saving mechanism.
 */
export enum SavingMechanism {
  /**
   * Classic full saving that loads and persists editors as a `Save`.
   * The mutations for persisting are `save` and `submit`.
   */
  FULL = "FULL",
  /**
   * New Block based persistence that loads and saves Work contents as Answer
   * Blocks. The Answer Blocks can be a classic editor with a well-known
   * `block_name`, an editor for extended answer question, or a block for other
   * types of questions.
   *
   * Each block is saved independently as a `BlockSnapshot` using
   * `saveBlockSnapshot` mutation. They are submitted using `submitV2` mutation
   * that links a `Submission` with Block Snapshots instead of a `Save`.
   */
  BLOCK = "BLOCK",
}

export interface AuthorityState {
  /** The current assessment ID. */
  assessmentId: string;

  /** The current work ID. */
  workId: string;

  /** Blanket work lock on the current session to prevent Save inconsistencies. */
  sessionLock: boolean;

  /** Computed saving mechanism. */
  savingMechanism: SavingMechanism;

  /** Work has a draft submission. */
  hasDraft: boolean;

  /** Work has a final submission. */
  hasFinal: boolean;

  /** Whether the current work is group work (true) or individual work (false). */
  isGroupWork: boolean;

  /**
   * Next submit attempt number in the Work.
   *
   * Any submit attempt made will contain this number, that acts like a mutex to
   * prevent multiple clients recording the same submit attempt. It starts with
   * 0, and increments with every successful submit attempt. One counter for
   * both DRAFT and FINAL submission attempts.
   *
   * Value is set when the Work is ready in the redux state.
   */
  submitAttemptCount: number | null;

  /** The last save request has an error */
  saveError: SaveError | null;

  /** Number of attempts the save request queue is at. Starts with 0. */
  saveAttempt: number;

  /** Save requests' Redux requestId that are still in flights*/
  savesInFlight: string[];

  /** The current status of the connection to the server */
  connectionStatus: ConnectionStatus;

  /**
   * Whether the student has accepted the submission delcarations.
   * The declarations include academic integrity and generative AI.
   */
  hasAcceptedSubmissionDeclarations: {
    academicIntegrity: boolean;
    generativeAi: boolean;
  };

  /** A submit request is in flight. */
  submitLoading: boolean;
  /** The last submit request has errored out. */
  submitError: SubmitError | null;
  /** Show a submission preview page. */
  submitPreview: boolean;
  /** Show a timed submit prompt locking the submission date. */
  submitPromptOpened: string | null;

  /** Local record of all latest answer snapshots */
  snapshots: Record<string, Snapshot>;

  /** Queue of unsaved Answer blocks and their snapshots */
  pendingSnapshots: PendingSnapshot[];

  /**
   * The current Work is either stale or unable to apply changes. Full refresh
   * needed.
   */
  documentError: boolean;
}

/** Error returned from submission process. */
export interface SubmitError {
  kind: "validation" | "network" | "already_reported";
  title: string;
  detail?: string;
}

/** Error returned from snapshot saving process. */
export interface SaveError {
  kind: "network" | "already_reported" | "unauthorized";
  title: string;
  detail?: string;
}

/** Whether the given SaveError is retryable. */
export function isRetryableSaveError(saveError: SaveError) {
  return saveError.kind === "network";
}

/** Snapshot belonging to an Answer Block. */
export interface PendingSnapshot {
  /** The answer block id to save the snapshot to. */
  answerBlockId: string;
  /** Snapshot state */
  snapshot: Snapshot;
}
